import React, { useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TabView, TabPanel } from 'primereact/tabview';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { InputSwitch } from 'primereact/inputswitch';
import { Button } from 'primereact/button';
import ConceptDetailsSection from 'screens/FDP/components/ConceptDetails';
import NotesSection from 'screens/FDPScreen/components/NotesSection';
import NDCHeader from 'screens/FDPScreen/components/UnconstraintForecastScreen/components/NDCHeader';
import CopyIcon from 'assets/icons/copyIcon.svg';
import ResetIcon from 'assets/icons/revertIcon.svg';
import classes from './unconstrainedEdit.module.scss';
import {
  getFutureMonths15,
  mockConceptDetails,
  mockContractDetails,
  mockNotesData,
  mockDirectNDCs,
  mockIndirectNDCs,
  mockForecastData,
  mockInvoiceSales,
} from '../unconstrainedMockData';

const formatNumber = (val: number): string => val.toLocaleString('en-US');

const UnconstrainedForecastEdit: React.FC = () => {
  const navigate = useNavigate();
  const forecastingMonths = useMemo(() => getFutureMonths15(), []);

  const [directCurrentNDCIndex, setDirectCurrentNDCIndex] = useState(0);
  const [indirectCurrentNDCIndex, setIndirectCurrentNDCIndex] = useState(0);
  const [isPercentage, setIsPercentage] = useState(false);
  const [useOldValues, setUseOldValues] = useState(false);

  // Editable Adj Forecast values: NDC11 -> number[15]
  const [editValues, setEditValues] = useState<Map<string, number[]>>(() => {
    const map = new Map<string, number[]>();
    [...mockDirectNDCs, ...mockIndirectNDCs].forEach((ndc) => {
      const entry = mockForecastData.find((f) => f.NDC11 === ndc.ndc);
      const values = entry
        ? entry.forecast_details.map((d) => d.UnconstrainedForecast)
        : new Array(15).fill(0);
      map.set(ndc.ndc, values);
    });
    return map;
  });

  // Copy tracking: Set of "ndcKey+idx" strings
  const [copyInfo, setCopyInfo] = useState<Set<string>>(new Set());

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const getInvoiceSalesDetails = (ndcKey: string, metric: 'ytd' | '3ma' | 'pm'): string => {
    const entry = mockInvoiceSales.find((s) => s.NDC11 === ndcKey);
    if (!entry) return '-';
    if (metric === 'ytd') return formatNumber(entry.YTDAverage);
    if (metric === '3ma') return formatNumber(entry.ThreeMonthAverage);
    return formatNumber(entry.LastMonthAverage);
  };

  const getValueForNdc = (ndcKey: string, idx: number): string => {
    const arr = editValues.get(ndcKey);
    if (!arr) return '-';
    return (arr[idx] ?? 0).toLocaleString('en-US');
  };

  const getBaseline = (ndcKey: string, dateString: string): string => {
    const entry = mockForecastData.find((f) => f.NDC11 === ndcKey);
    const detail = entry?.forecast_details.find((d) => d.ForecastPeriod === dateString);
    return detail ? formatNumber(detail.WholesalerDemandForecast) : '-';
  };

  const handleCellChange = useCallback((ndcKey: string, idx: number, value: string) => {
    const parsed = parseInt(value.replace(/,/g, '')) || 0;
    setEditValues((prev) => {
      const next = new Map(prev);
      const arr = [...(next.get(ndcKey) ?? new Array(15).fill(0))];
      arr[idx] = parsed;
      next.set(ndcKey, arr);
      return next;
    });
  }, []);

  const handleCopyValues = useCallback((ndcKey: string, idx: number) => {
    setEditValues((prev) => {
      const next = new Map(prev);
      const arr = [...(next.get(ndcKey) ?? new Array(15).fill(0))];
      const val = arr[idx];
      for (let i = idx + 1; i < 15; i++) {
        arr[i] = val;
      }
      next.set(ndcKey, arr);
      return next;
    });
    setCopyInfo((prev) => {
      const next = new Set(prev);
      for (let i = idx; i < 15; i++) next.add(`${ndcKey}${i}`);
      return next;
    });
  }, []);

  const handleResetValues = useCallback((ndcKey: string, idx: number) => {
    const entry = mockForecastData.find((f) => f.NDC11 === ndcKey);
    if (!entry) return;
    setEditValues((prev) => {
      const next = new Map(prev);
      const arr = [...(next.get(ndcKey) ?? new Array(15).fill(0))];
      for (let i = idx; i < 15; i++) {
        arr[i] = entry.forecast_details[i]?.UnconstrainedForecast ?? 0;
      }
      next.set(ndcKey, arr);
      return next;
    });
    setCopyInfo((prev) => {
      const next = new Set(prev);
      for (let i = idx; i < 15; i++) next.delete(`${ndcKey}${i}`);
      return next;
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Per-NDC content renderer
  // ---------------------------------------------------------------------------

  const renderNDCContent = (ndcKey: string, packSize: string) => (
    <div className={classes.contentContainer}>
      {/* Plan header row */}
      <div className={classes.forecastContainer}>
        <h4>
          <span>
            Plan {forecastingMonths[0].label} – {forecastingMonths[14].label}{' '}
            <span style={{ color: 'green' }}>*</span>
          </span>
        </h4>
        <div className={classes.switcherContainer}>
          <span className={classes.btnLable}>Number</span>
          <InputSwitch
            checked={isPercentage}
            onChange={(e) => setIsPercentage(!!e.value)}
          />
          <span className={classes.btnLable}>Percent</span>
        </div>
        <div className={classes.switcherContainer}>
          <span className={classes.btnLable}>Old Values</span>
          <InputSwitch
            checked={useOldValues}
            onChange={(e) => setUseOldValues(!!e.value)}
          />
          <span className={classes.btnLable}>Forecast Values</span>
        </div>
      </div>

      <hr />

      {/* Pack Size + YTD/3MA/PM */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-evenly',
          gap: '5rem',
          padding: '1rem',
        }}
      >
        <div>
          <p>
            Pack Size: <span>{packSize}</span>
          </p>
        </div>
        <div className={classes.saleDetails}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: '600', paddingLeft: '3rem' }}>
              YTD: {getInvoiceSalesDetails(ndcKey, 'ytd')}
            </div>
            <div className={classes.vertical}></div>
            <div style={{ fontSize: '14px', fontWeight: '600' }}>
              3MA: {getInvoiceSalesDetails(ndcKey, '3ma')}
            </div>
            <div className={classes.vertical}></div>
            <div style={{ fontSize: '14px', fontWeight: '600', paddingRight: '3rem' }}>
              PM: {getInvoiceSalesDetails(ndcKey, 'pm')}
            </div>
          </div>
        </div>
      </div>

      {/* Forecast table */}
      <div className={classes.forecastTable}>
        <table>
          <thead>
            <tr>
              <th style={{ fontSize: '15px' }}>Forecast Period</th>
              <th style={{ fontSize: '15px' }}>Baseline</th>
              <th style={{ fontSize: '15px' }}>Adj Forecast</th>
              <th style={{ width: '6rem' }}></th>
            </tr>
          </thead>
          <tbody>
            {forecastingMonths.map((month, idx) => (
              <tr
                key={month.dateString}
                className={month.isExtended ? classes.extendedMonth : undefined}
              >
                <td style={{ width: '10rem' }}>{month.label}</td>
                <td style={{ width: '15rem' }}>
                  {getBaseline(ndcKey, month.dateString)}
                </td>
                <td style={{ width: '10rem' }}>
                  <input
                    style={{
                      width: '15rem',
                      height: '2rem',
                      border: '1px solid #bababa',
                      fontSize: '0.9rem',
                      padding: '0.3rem 0',
                      paddingLeft: '0.5rem',
                      borderRadius: '0.3rem',
                      margin: 0,
                    }}
                    type="text"
                    value={getValueForNdc(ndcKey, idx)}
                    onChange={(e) => handleCellChange(ndcKey, idx, e.target.value)}
                  />
                  {isPercentage && <span style={{ fontWeight: '600' }}> %</span>}
                </td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Button
                      style={{ height: '28px', width: '28px' }}
                      icon={
                        <img
                          src={CopyIcon}
                          style={{ height: '20px', width: '20px', cursor: 'pointer', opacity: '0.8' }}
                        />
                      }
                      onClick={() => handleCopyValues(ndcKey, idx)}
                      rounded
                      text
                      aria-label="Copy down"
                    />
                    {copyInfo.has(`${ndcKey}${idx}`) ? (
                      <Button
                        style={{ height: '28px', width: '28px' }}
                        icon={
                          <img
                            src={ResetIcon}
                            style={{ height: '20px', width: '20px', cursor: 'pointer', opacity: '0.8' }}
                          />
                        }
                        onClick={() => handleResetValues(ndcKey, idx)}
                        rounded
                        text
                        aria-label="Reset"
                      />
                    ) : (
                      <div style={{ width: '28px' }} />
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Per-accordion Save / Finish */}
      <div className={classes.categoryButtonGroup}>
        <Button label="Save" className={classes.saveBtnStyle} />
        <Button label="Finish" className={classes.finishBtnStyle} />
      </div>
    </div>
  );

  // ---------------------------------------------------------------------------
  // Main render
  // ---------------------------------------------------------------------------

  return (
    <div className={classes.viewContainer}>
      {/* Concept Details */}
      <div className={classes.conceptContainer}>
        <div className={classes.heading}>
          <h2>Unconstrained Forecast</h2>
        </div>
        <div className={classes.conceptInfo}>
          <div style={{ width: '100%' }}>
            <ConceptDetailsSection
              conceptDetails={mockConceptDetails}
              contractDetails={mockContractDetails}
              mode="edit"
              path="/unconstraint_forecast/edit"
            />
          </div>
        </div>
      </div>

      {/* Main content row: accordions + NotesSection */}
      <div style={{ display: 'flex', gap: '1rem', padding: '.5rem' }}>
        <div
          style={{
            display: 'flex',
            flexGrow: 1,
            background: 'var(--bg-card)',
            color: 'var(--text-primary)',
            width: '0rem',
          }}
        >
          <div
            style={{
              width: '100%',
              border: '1px solid #E5E7EB',
              paddingTop: '1rem',
              paddingBottom: '1rem',
              borderRadius: '8px',
            }}
          >
            <div style={{ padding: '1rem' }}>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Direct Accordion */}
                {mockDirectNDCs.length > 0 && (
                  <Accordion
                    className={classes.accordianComponent}
                    activeIndex={0}
                  >
                    <AccordionTab header="Direct" className={classes.accordianTabComponent}>
                      <div className={classes.directtabContainer}>
                        <TabView
                          scrollable
                          style={{ width: '100%' }}
                          tabIndex={directCurrentNDCIndex}
                          onTabChange={(e) => setDirectCurrentNDCIndex(e.index)}
                        >
                          {mockDirectNDCs.map((ndc, index) => (
                            <TabPanel
                              key={ndc.ndc}
                              header={
                                <NDCHeader
                                  heading={ndc.ndc}
                                  isSelected={directCurrentNDCIndex === index}
                                  tabIndex={index}
                                />
                              }
                            >
                              {renderNDCContent(ndc.ndc, ndc.packSize)}
                            </TabPanel>
                          ))}
                        </TabView>
                      </div>
                    </AccordionTab>
                  </Accordion>
                )}

                {/* Indirect Accordion */}
                {mockIndirectNDCs.length > 0 && (
                  <Accordion
                    className={classes.accordianComponent}
                    activeIndex={mockDirectNDCs.length === 0 ? 0 : null}
                  >
                    <AccordionTab header="Indirect" className={classes.accordianTabComponent}>
                      <div className={classes.directtabContainer}>
                        <TabView
                          scrollable
                          style={{ width: '100%' }}
                          tabIndex={indirectCurrentNDCIndex}
                          onTabChange={(e) => setIndirectCurrentNDCIndex(e.index)}
                        >
                          {mockIndirectNDCs.map((ndc, index) => (
                            <TabPanel
                              key={ndc.ndc}
                              header={
                                <NDCHeader
                                  heading={ndc.ndc}
                                  isSelected={indirectCurrentNDCIndex === index}
                                  tabIndex={index}
                                />
                              }
                            >
                              {renderNDCContent(ndc.ndc, ndc.packSize)}
                            </TabPanel>
                          ))}
                        </TabView>
                      </div>
                    </AccordionTab>
                  </Accordion>
                )}
              </div>
            </div>

            {/* Bottom button group */}
            <div className={classes.bottomBtngroup}>
              <div>
                <Button label="Show Summary" severity="secondary" outlined />
                <Button label="Cancel" severity="danger" outlined />
              </div>
              <div>
                <Button
                  label="Finish All"
                  style={{ backgroundColor: '#029999', border: 'none' }}
                  onClick={() => navigate('/fdp-updated/sales_plan_updated/edit')}
                />
              </div>
            </div>
          </div>
        </div>

        <NotesSection
          mode="edit"
          isNotesDataLoading={false}
          notesData={mockNotesData}
        />
      </div>
    </div>
  );
};

export default UnconstrainedForecastEdit;
